import React, { Fragment, FunctionComponent, useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import { StepperContent, StepperHeader, StepperSubmit } from '../../../../../shared/general';
import {
  utilsCalculatorCalculateBuyerCostsGetDefaultValuesStep1,
  utilsCalculatorCalculateBuyerCosts
} from '../../utils';
import { FormElementDynamicInputTyped } from '../../../../../shared/form-elements';
import {
  CalculatorBuyerCostsFormFields,
  CalculatorBuyerCostsResultData,
  CalculatorsBuyerCostsStep1FormFields
} from '../../types';
import { utilNumberToCurrency, utilStringToNumber } from '../../../../../../utils';
import { useFormContext } from '../../../../../../hooks';
import { NhgContext } from '../../calculator-buyer-costs';
import { useIntl } from 'react-intl';
import { useCalculatorBuyerCostsStep1FormFields } from '../../config';

export const CalculatorBuyerCostsStepperStep1: FunctionComponent<{
  id: number;
  title?: string;
}> = ({ id, title }) => {
  const intl = useIntl();
  const calculatorBuyerCostsStep1FormFields = useCalculatorBuyerCostsStep1FormFields(intl);
  const nhg = useContext(NhgContext);
  const [completedText, setCompletedText] = useState('');
  const { form, updateForm, updateResult, setError, isLoading, setLoading } = useFormContext<
    CalculatorBuyerCostsResultData,
    CalculatorBuyerCostsFormFields
  >();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid }
  } = useForm({
    mode: 'onBlur',
    defaultValues: utilsCalculatorCalculateBuyerCostsGetDefaultValuesStep1(intl)
  });

  const { isNewConstruction } = watch();

  const onSubmit = async (formData: Record<keyof CalculatorsBuyerCostsStep1FormFields, string>) => {
    setLoading(true);
    try {
      if (formData.isNewConstruction === 'true') {
        const result = utilsCalculatorCalculateBuyerCosts(
          { ...form, ...formData } as CalculatorBuyerCostsFormFields,
          nhg
        );
        updateResult(result);
      }
      updateForm(formData);
      const { housePrice } = formData;
      setCompletedText(utilNumberToCurrency(utilStringToNumber(housePrice), true, 0));
      setLoading(false);
    } catch (e) {
      setError(true);
      setLoading(false);
      // Handle error
    }
  };

  if (isLoading) {
    return <div>Loading...</div>; // Or a more sophisticated loading indicator
  }

  return (
    <Fragment>
      <StepperHeader
        onClose={handleSubmit(onSubmit)}
        title={title}
        id={id}
        completedText={completedText}
      />
      <StepperContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormElementDynamicInputTyped<CalculatorsBuyerCostsStep1FormFields>
            formFields={calculatorBuyerCostsStep1FormFields}
            id={'housePrice'}
            register={register}
            variant={`calculator`}
            errors={errors}
            values={watch()}
            options={{ required: true }}
          />

          <FormElementDynamicInputTyped<CalculatorsBuyerCostsStep1FormFields>
            formFields={calculatorBuyerCostsStep1FormFields}
            id={'isNewConstruction'}
            register={register}
            errors={errors}
            values={watch()}
            options={{ required: true }}
          />

          {isNewConstruction === 'false' && (
            <FormElementDynamicInputTyped<CalculatorsBuyerCostsStep1FormFields>
              formFields={calculatorBuyerCostsStep1FormFields}
              id={'withPartner'}
              register={register}
              errors={errors}
              values={watch()}
              options={{ required: true }}
            />
          )}
          <StepperSubmit id={id} isValidated={isValid} />
        </form>
      </StepperContent>
    </Fragment>
  );
};
